import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import * as React from 'react';
import { DownOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import { v4 } from 'uuid';
import { SelectWrap, SelectHead, SelectHeadInput, SelectHeadText, SelectOptionWrap, SelectOption, HiddenSelect } from './styles.js';
import { jsx } from '@emotion/react';
import usePrevious from '../../../hooks/use-previous/use-previous.js';

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it["return"] != null) it["return"](); } finally { if (didErr) throw err; } } }; }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var OptionInBoundsStatus;
(function (OptionInBoundsStatus) {
  OptionInBoundsStatus[OptionInBoundsStatus["ABOVE"] = 0] = "ABOVE";
  OptionInBoundsStatus[OptionInBoundsStatus["OKAY"] = 1] = "OKAY";
  OptionInBoundsStatus[OptionInBoundsStatus["BELOW"] = 2] = "BELOW";
})(OptionInBoundsStatus || (OptionInBoundsStatus = {}));
var fontSizeMap = {
  s: 10,
  m: 12,
  l: 14
};
var defaultSelect = function defaultSelect() {
  var placeholder = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return {
    label: placeholder,
    value: null,
    optionId: 0.1
  };
};
var defaultMultiselectText = {
  noneSelected: 'No Filter Selected',
  singular: 'Filter Selected',
  plural: 'Filters Selected'
};
var Select = function Select(_ref) {
  var value = _ref.value,
      id = _ref.id,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? [] : _ref$options,
      _ref$disableToggle = _ref.disableToggle,
      disableToggle = _ref$disableToggle === void 0 ? false : _ref$disableToggle,
      _ref$filterSelect = _ref.filterSelect,
      filterSelect = _ref$filterSelect === void 0 ? false : _ref$filterSelect,
      _ref$multiselect = _ref.multiselect,
      multiselect = _ref$multiselect === void 0 ? false : _ref$multiselect,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'l' : _ref$size,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$fullWidth = _ref.fullWidth,
      fullWidth = _ref$fullWidth === void 0 ? false : _ref$fullWidth,
      dataTestId = _ref.dataTestId,
      _ref$multiselectText = _ref.multiselectText,
      multiselectText = _ref$multiselectText === void 0 ? defaultMultiselectText : _ref$multiselectText,
      handleChange = _ref.handleChange,
      onBlur = _ref.onBlur,
      onFocus = _ref.onFocus,
      className = _ref.className,
      hasError = _ref.hasError,
      _ref$placeholder = _ref.placeholder,
      placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder;
  var formatOptions = React.useCallback(function () {
    return options.map(function (o, i) {
      return _objectSpread(_objectSpread({}, o), {}, {
        optionId: i
      });
    });
  }, [options]);
  var placeholderRef = React.useRef(defaultSelect(placeholder));
  var filterSelectInput = React.useRef(null);
  var selectOptionsWrap = React.useRef(null);
  var uniqueId = React.useRef(id !== null && id !== void 0 ? id : "select_id-".concat(v4()));
  var _React$useState = React.useState(formatOptions()),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      optionsList = _React$useState2[0],
      setOptionsList = _React$useState2[1];
  var _React$useState3 = React.useState([placeholderRef.current]),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      selected = _React$useState4[0],
      setSelected = _React$useState4[1];
  var _React$useState5 = React.useState(false),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      isOpen = _React$useState6[0],
      setOpen = _React$useState6[1];
  var _React$useState7 = React.useState(null),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      navigationIndex = _React$useState8[0],
      setNavigationIndex = _React$useState8[1];
  var _React$useState9 = React.useState(''),
      _React$useState10 = _slicedToArray(_React$useState9, 2),
      filterText = _React$useState10[0],
      setFilterText = _React$useState10[1];
  var _React$useState11 = React.useState(false),
      _React$useState12 = _slicedToArray(_React$useState11, 2),
      forceBlueBorder = _React$useState12[0],
      setForceBlueBorder = _React$useState12[1];
  var selectedRef = usePrevious(selected);
  var valueRef = usePrevious(value);
  React.useEffect(function () {
    var isEmptyValue = !isNumber(value) && isEmpty(value);
    var isEmptyValueRef = !isNumber(valueRef) && isEmpty(valueRef);
    if (isEmptyValue && isEmptyValue !== isEmptyValueRef) {
      if (selected !== value) {
        setSelected([placeholderRef.current]);
      }
    }
  }, [value, valueRef, selected]);
  React.useEffect(function () {
    var valueIntoArray = Array.isArray(value) ? value : [value];
    if (!multiselect && valueIntoArray.length > 1) {
      console.error('Value failed to assign. An array of multiple values for value prop can only be used with multiselect');
    }
  }, [multiselect, value]);
  function emitChange(option) {
    var selectedOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : selected;
    if (handleChange) {
      var optionsToEmit = [option].map(function (_ref2) {
        var label = _ref2.label,
            value = _ref2.value;
        return {
          label: label,
          value: value
        };
      });
      if (multiselect) {
        optionsToEmit = [].concat(_toConsumableArray(optionsToEmit), _toConsumableArray(selectedOptions.map(function (_ref3) {
          var label = _ref3.label,
              value = _ref3.value;
          return {
            label: label,
            value: value
          };
        })));
      }
      if (optionsToEmit.length > 1) {
        optionsToEmit = optionsToEmit.filter(function (o) {
          return o.value !== null;
        });
      }
      handleChange(optionsToEmit);
    }
  }
  var toggleOption = function toggleOption(option, toggled, optionId) {
    if (!multiselect) {
      setFilterText(option.label);
    }
    if (toggled) {
      if (disableToggle) {
        setOpen(false);
        return;
      }
      var newSelectedList = selected.filter(function (s) {
        return s.optionId !== optionId;
      });
      setSelected(newSelectedList.length ? newSelectedList : [placeholderRef.current]);
      setFilterText('');
      if (!multiselect) {
        setOpen(false);
      }
      emitChange(placeholderRef.current, newSelectedList);
      return;
    }
    emitChange(option);
    if (multiselect) {
      setSelected([].concat(_toConsumableArray(selected.filter(function (s) {
        return s.optionId !== null;
      })), [option]));
      setFilterText('');
      return;
    }
    setSelected([option]);
    setOpen(false);
  };
  function handleSelectHeadKeyPress(event) {
    if (disabled) {
      return;
    }
    if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    switch (event.key) {
      case 'Enter':
        if (isOpen && navigationIndex !== null) {
          var o = optionsList[navigationIndex];
          toggleOption(o, selected.some(function (s) {
            return s.optionId === o.optionId;
          }), o.optionId);
        }
        if (!multiselect) {
          setOpen(!isOpen);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen && navigationIndex !== null && navigationIndex > 0) {
          setNavigationIndex(navigationIndex - 1);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setOpen(true);
          break;
        }
        if (navigationIndex !== null && navigationIndex < optionsList.length - 1) {
          setNavigationIndex(navigationIndex + 1);
        }
        if (navigationIndex === null && optionsList.length) {
          setNavigationIndex(0);
        }
        break;
    }
  }
  function filterSelectIsFocused() {
    if (filterSelectInput && filterSelectInput.current) {
      return filterSelectInput.current === document.activeElement;
    }
    return false;
  }
  function getDisplayValueForSelected() {
    var isInput = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    if (filterSelectIsFocused() && isInput) {
      return filterText;
    }
    var _selected = _slicedToArray(selected, 1),
        singleSelected = _selected[0];
    if (!singleSelected) {
      throw Error('Selected value is undefined');
    }
    if (!multiselect) {
      return singleSelected.label;
    }
    if (isInput) {
      return '';
    }
    if (selected.some(function (x) {
      return x.value === null;
    })) {
      return multiselectText.noneSelected;
    }
    return "".concat(selected.length, " ").concat(selected.length > 1 ? multiselectText.plural : multiselectText.singular);
  }
  var handleClickListener = function handleClickListener(e) {
    var target = e.target;
    var selectHeadClick = target && target.parentElement ? target.parentElement.id : null;
    var optionsClick = target && target.parentElement && target.parentElement.offsetParent ? target.parentElement.offsetParent.id : null;
    if (selectHeadClick !== uniqueId.current && optionsClick !== uniqueId.current) {
      setOpen(false);
    }
  };
  var getOptionsInBoundsStatus = React.useCallback(function () {
    if (selectOptionsWrap && selectOptionsWrap.current) {
      var wrapper = selectOptionsWrap.current;
      var option = navigationIndex !== null && selectOptionsWrap.current.children[navigationIndex];
      if (wrapper && option) {
        var inUpperLimit = wrapper.scrollTop <= option.offsetTop;
        var inLowerLimit = wrapper.scrollTop + wrapper.offsetHeight >= option.offsetTop + option.offsetHeight;
        if (!inUpperLimit) {
          return OptionInBoundsStatus.ABOVE;
        }
        if (!inLowerLimit) {
          return OptionInBoundsStatus.BELOW;
        }
      }
    }
    return OptionInBoundsStatus.OKAY;
  }, [navigationIndex]);
  var scrollToHighlightedOption = React.useCallback(function () {
    var optionInBoundStatus = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : OptionInBoundsStatus.ABOVE;
    if (selectOptionsWrap && selectOptionsWrap.current) {
      var wrapper = selectOptionsWrap.current;
      var option = navigationIndex !== null && selectOptionsWrap.current.children[navigationIndex];
      if (wrapper && option) {
        if (optionInBoundStatus === OptionInBoundsStatus.ABOVE) {
          wrapper.scrollTop = option.offsetTop;
        }
        if (optionInBoundStatus === OptionInBoundsStatus.BELOW) {
          wrapper.scrollTop = option.offsetTop - wrapper.offsetHeight + option.offsetHeight;
        }
      }
    }
  }, [navigationIndex]);
  function focusFilterInput() {
    if (filterSelectInput && filterSelectInput.current) {
      filterSelectInput.current.focus();
      setForceBlueBorder(true);
    }
  }
  function blurFilterInput() {
    if (filterSelectInput && filterSelectInput.current) {
      filterSelectInput.current.blur();
      setForceBlueBorder(false);
    }
  }
  function handleOnFocus() {
    setFilterText('');
    if (onFocus) {
      onFocus();
    }
  }
  function handleOnBlur() {
    blurFilterInput();
    if (onBlur) {
      onBlur();
    }
  }
  React.useEffect(function () {
    window.addEventListener('click', handleClickListener);
    return function () {
      return window.removeEventListener('click', handleClickListener);
    };
  }, []);
  React.useEffect(function () {
    scrollToHighlightedOption();
    if (isOpen === false) {
      if (selected.length > 1) {
        setFilterText('');
        setOptionsList(formatOptions());
      }
    }
    if (isOpen) {
      focusFilterInput();
      if (selectedRef !== selected && selected[0]) {
        setNavigationIndex(selected[0].optionId);
      }
    }
  }, [isOpen, formatOptions, scrollToHighlightedOption, selected, selectedRef]);
  React.useEffect(function () {
    if (!filterSelect) {
      return;
    }
    var filteredOptions = formatOptions().filter(function (_ref4) {
      var label = _ref4.label;
      return label.slice(0, filterText.length).toLowerCase() === filterText.toLowerCase();
    });
    setOptionsList(filteredOptions);
    setNavigationIndex(null);
  }, [filterText, filterSelect, formatOptions]);
  React.useEffect(function () {
    var valueIntoArray = Array.isArray(value) ? value : [value];
    var isEmptyValue = !isNumber(value) && isEmpty(value);
    if (value !== valueRef && !isEmptyValue) {
      var updatedSelected = [];
      var _iterator = _createForOfIteratorHelper(valueIntoArray),
          _step;
      try {
        var _loop = function _loop() {
          var v = _step.value;
          var optionWithValue = optionsList.find(function (o) {
            return isEqual(o.value, v);
          });
          if (!optionWithValue) {
            console.warn("Could not find option with matching value ".concat(v));
            return "continue";
          }
          updatedSelected.push(optionWithValue);
        };
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var _ret = _loop();
          if (_ret === "continue") continue;
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
      setSelected(updatedSelected);
    }
  }, [value, valueRef, multiselect, optionsList, selected]);
  React.useEffect(function () {
    var optionsInBoundStatus = getOptionsInBoundsStatus();
    scrollToHighlightedOption(optionsInBoundStatus);
  }, [navigationIndex, getOptionsInBoundsStatus, scrollToHighlightedOption]);
  React.useMemo(function () {
    setOptionsList(formatOptions());
  }, [formatOptions]);
  return jsx(SelectWrap, {
    className: className,
    "data-testid": "select-wrap",
    fullWidth: fullWidth,
    id: uniqueId.current,
    size: size
  }, jsx(SelectHead, {
    "data-testid": dataTestId || 'select-head',
    disabled: disabled,
    filled: selected.some(function (x) {
      return x.value !== null;
    }),
    forceFocusBorder: forceBlueBorder,
    hasError: hasError,
    onBlur: handleOnBlur,
    onClick: function onClick() {
      return !disabled ? setOpen(!isOpen) : null;
    },
    onFocus: handleOnFocus,
    onKeyDown: function onKeyDown(e) {
      return handleSelectHeadKeyPress(e);
    },
    size: size,
    tabIndex: 0
  }, !multiselect && filterSelect && jsx(SelectHeadInput, {
    ref: filterSelectInput,
    "data-testid": "select-head-input",
    disabled: disabled,
    onChange: function onChange(e) {
      return setFilterText(e.target.value);
    },
    onFocus: function onFocus() {
      return setForceBlueBorder(true);
    },
    tabIndex: -1,
    value: filterSelectIsFocused() && isOpen ? filterText : getDisplayValueForSelected()
  }), (multiselect || !filterSelect) && jsx(SelectHeadText, {
    "data-testid": "select-head-text"
  }, getDisplayValueForSelected(false)), jsx(DownOutlined, {
    style: {
      fontSize: (fontSizeMap === null || fontSizeMap === void 0 ? void 0 : fontSizeMap[size]) || 14
    }
  })), isOpen && jsx(SelectOptionWrap, {
    ref: selectOptionsWrap,
    "data-testid": "options-wrap",
    fullWidth: fullWidth,
    size: size
  }, multiselect && filterSelect && jsx(SelectHeadInput, {
    ref: filterSelectInput,
    "data-testid": "select-head-input-multiselect",
    disabled: disabled,
    isMultiselect: true,
    onChange: function onChange(e) {
      return setFilterText(e.target.value);
    },
    onFocus: function onFocus() {
      return setForceBlueBorder(true);
    },
    onKeyDown: function onKeyDown(e) {
      return handleSelectHeadKeyPress(e);
    },
    value: filterSelectIsFocused() ? filterText : getDisplayValueForSelected()
  }), React.Children.toArray(optionsList.map(function (option, i) {
    var isSelected = selected.some(function (s) {
      return isEqual(s.value, option.value);
    });
    return jsx(SelectOption, {
      "data-testid": "select-option-".concat(i),
      navigationHighlight: i === navigationIndex,
      onClick: function onClick() {
        return toggleOption(option, isSelected, option.optionId);
      },
      selected: isSelected,
      size: size
    }, option.label);
  }))), jsx(HiddenSelect, {
    disabled: true,
    multiple: true,
    value: selected.map(function (x) {
      return x.value;
    })
  }));
};
Select.displayName = 'Select';

export { Select, defaultMultiselectText, defaultSelect };
//# sourceMappingURL=select.js.map
